import { useQuery } from "@tanstack/react-query";
import { Alert, Box, Chip, Paper, Typography, CircularProgress } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { getTargetExemplar } from "../../api/client";
import ExemplarOutput from "./outputs/ExemplarOutput";

const COVERAGE_LABEL_KEY: Record<string, string> = {
  full: "ExemplarPanel.coverageFull",
  role_only: "ExemplarPanel.coverageRoleOnly",
  none: "ExemplarPanel.coverageNone",
};

export default function ExemplarPanel({ targetId }: { targetId: string }) {
  const { t } = useTranslation("resume");
  const { data, isLoading } = useQuery({
    queryKey: ["exemplar", targetId],
    queryFn: () => getTargetExemplar(targetId),
  });

  if (isLoading) return <CircularProgress size={20} />;
  if (!data) return null;

  if (data.coverage === "none" || !data.exemplar) {
    return (
      <Paper sx={{ p: 2 }} variant="outlined">
        <Typography variant="subtitle1">{t("ExemplarPanel.title")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("ExemplarPanel.notAvailable")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }} variant="outlined">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="subtitle1">{t("ExemplarPanel.title")}</Typography>
        <Chip size="small" label={t(COVERAGE_LABEL_KEY[data.coverage])} />
        {data.refreshed_at && (
          <Typography variant="caption" color="text.secondary">
            {t("ExemplarPanel.asOf", { date: new Date(data.refreshed_at).toLocaleDateString() })}
          </Typography>
        )}
      </Box>
      {data.company_overlay_gated && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <Trans
            i18nKey="ExemplarPanel.upsell"
            t={t}
            values={{
              companyLabel: data.matched_company
                ? `${data.matched_company}-specific`
                : "company-specific",
            }}
            components={[<strong key="strong" />]}
          />
        </Alert>
      )}
      <ExemplarOutput step={{ output: data.exemplar,
        narrative_markdown: data.exemplar.narrative_markdown ?? null,
        created_at: data.refreshed_at }} />
    </Paper>
  );
}
